/* eslint-disable no-unsafe-optional-chaining */
import {
  PrismicAllBlogPostsQuery,
  BlogPostFragment,
} from '@prismic/gqlTypes/prismicOperations';

import { Tag } from '@prismic/gqlTypes/prismicTypes';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { NextPage } from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';

import { mapEdgesToItems } from '@utils/maps';

import Page from './Page';
import { ISortOptions, sortOptions } from './sortOptions';

const getSortedBlogPosts = (
  allPosts: BlogPostFragment[],
  sortByValue: string,
  _sortOptions: ISortOptions
): BlogPostFragment[] => {
  if (sortByValue === _sortOptions.firstPublicationDateASC.value) {
    // console.log("from old to new ASC");
    const arr = allPosts.sort((a, b) => {
      const aDate = new Date(a._meta.firstPublicationDate).getTime();
      const bDate = new Date(b._meta.firstPublicationDate).getTime();
      return aDate - bDate;
    });
    return arr;
  }
  if (sortByValue === _sortOptions.firstPublicationDateDESC.value) {
    // console.log("from new to old DESC");
    return allPosts;
  }
  return allPosts;
};

export interface ViewProps {
  data: PrismicAllBlogPostsQuery;
  seo: NextSeoProps | null;
}

const View: NextPage<ViewProps> = ({ data, seo }) => {
  const [state, setState] = useState({
    allBlogPosts: null,
    allSortedBlogPosts: null,
    canLoadMore: false,
    postsPerPage: 0,
    postsToShow: null,
    sortBy: sortOptions.firstPublicationDateDESC,
    tagsList: null,
  });

  const allBlogPosts = useMemo(
    () =>
      mapEdgesToItems(data?.allBlog_posts).filter(
        (item) => item?.hide_for_blog_index_nav !== true
      ),
    [data]
  );

  const totalPosts = data?.allBlog_posts.totalCount;
  const title = data?.allBlog_homes.edges[0].node.title;

  const iconBannerSlice = useMemo(
    () =>
      data?.allBlog_homes.edges[0].node.body1.find(
        (slice) => slice.__typename === 'Blog_homeBody1Iconbanner'
      ),
    [data]
  );

  useEffect(() => {
    setState((prev) => {
      const postsPerPage = parseInt(
        data?.allBlog_homes?.edges[0].node.showitemsperpage,
        10
      );

      const tagsList = data?.allTags.edges.map((item) => ({
        isActive: false,
        tagName: item.node.name,
      }));

      return {
        ...prev,
        allBlogPosts,
        allSortedBlogPosts: allBlogPosts,
        canLoadMore: totalPosts > postsPerPage,
        postsPerPage,
        postsToShow: allBlogPosts?.slice(0, postsPerPage),
        tagsList,
      };
    });
  }, [data, allBlogPosts, totalPosts]);

  const loadMorePosts = useCallback(() => {
    setState((prev) => {
      const postsToShow = [
        ...prev.postsToShow,
        ...prev.allSortedBlogPosts?.slice(
          prev.postsToShow.length,
          prev.postsToShow.length + prev.postsPerPage
        ),
      ];

      return {
        ...prev,
        canLoadMore: postsToShow.length < prev.allSortedBlogPosts.length,
        postsToShow,
      };
    });
  }, []);

  const setPostsSorting = useCallback((value) => {
    setState((prev) => {
      const newAllSortedBlogPosts = prev.allSortedBlogPosts.sort((a, b) => {
        const aDate = new Date(a._meta.firstPublicationDate).getTime();
        const bDate = new Date(b._meta.firstPublicationDate).getTime();
        if (value.value === 'firstPublicationDateASC') {
          return aDate - bDate;
        }

        if (value.value === 'firstPublicationDateDESC') {
          return bDate - aDate;
        }

        return 0;
      });

      const newPostsToShow = newAllSortedBlogPosts.slice(
        0,
        prev.postsToShow.length
      );

      return {
        ...prev,
        allSortedBlogPosts: newAllSortedBlogPosts,
        postsToShow: newPostsToShow,
        sortBy: value,
      };
    });
  }, []);

  const featuresPosts = useMemo(() => {
    if (allBlogPosts) {
      return allBlogPosts.filter((item) => item.isfeatured === true);
    }
  }, [allBlogPosts]);

  const toggleTag = (tagName) => {
    setState((prev) => {
      const newTagsList = prev.tagsList.map((tag) => {
        if (tag.tagName === tagName) {
          return {
            isActive: !tag.isActive,
            tagName: tag.tagName,
          };
        }

        return tag;
      });

      if (newTagsList.every((t) => t.isActive === false)) {
        const _allSortedBlogPosts = getSortedBlogPosts(
          prev.allBlogPosts,
          prev.sortBy.value,
          sortOptions
        );

        return {
          ...prev,
          allSortedBlogPosts: _allSortedBlogPosts,
          canLoadMore: _allSortedBlogPosts.length > prev.postsPerPage,
          postsToShow: _allSortedBlogPosts.slice(0, prev.postsPerPage),
          tagsList: newTagsList,
        };
      }

      const newAllSortedBlogPosts = prev.allBlogPosts.filter((post) => {
        // all tags of this post
        const postTags = post.tags
          .map(({ tag }) => {
            const _tag = tag as Pick<Tag, 'name' | '_linkType'>;

            return _tag.name;
          })
          .join('')
          .toLowerCase();
        return newTagsList
          .filter((t) => t.isActive === true)
          .some((tag) => postTags.includes(tag.tagName.toLowerCase()));
      });

      const newAllSortedBlogPostsByDate = getSortedBlogPosts(
        newAllSortedBlogPosts,
        prev.sortBy.value,
        sortOptions
      );

      const newPostsToShow = newAllSortedBlogPosts.slice(0, prev.postsPerPage);
      const newCanLoadMore = newAllSortedBlogPosts.length > prev.postsPerPage;

      return {
        ...prev,
        allSortedBlogPosts: newAllSortedBlogPostsByDate,
        canLoadMore: newCanLoadMore,
        postsToShow: newPostsToShow,
        tagsList: newTagsList,
      };
    });
  };

  const clearSelectedTags = () => {
    setState((prev) => {
      const newTagsList = prev.tagsList.map((tag) => ({
        isActive: false,
        tagName: tag.tagName,
      }));

      const newAllSortedBlogPosts = getSortedBlogPosts(
        prev.allBlogPosts,
        prev.sortBy.value,
        sortOptions
      );
      const newPostsToShow = newAllSortedBlogPosts.slice(0, prev.postsPerPage);
      const newCanLoadMore = newAllSortedBlogPosts.length > prev.postsPerPage;
      return {
        ...prev,
        allSortedBlogPosts: newAllSortedBlogPosts,
        canLoadMore: newCanLoadMore,
        postsToShow: newPostsToShow,
        tagsList: newTagsList,
      };
    });
  };

  return (
    <>
      <NextSeo {...seo} />
      <Page
        title={title}
        posts={state.postsToShow}
        featuresPosts={featuresPosts}
        tagsList={state.tagsList}
        loadMorePosts={loadMorePosts}
        canLoadMore={state.canLoadMore}
        setPostsSorting={setPostsSorting}
        sortBy={state.sortBy}
        toggleTag={toggleTag}
        clearSelectedTags={clearSelectedTags}
        iconBannerSlice={iconBannerSlice}
      />
    </>
  );
};

View.displayName = 'BlogIndexPageView';

export default View;
